import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Separator } from '../../ui/separator';
import { Globe, Eye, RefreshCw } from 'lucide-react';

const TITLE_MAX = 60;
const DESC_MAX = 155;

const getCharacterCountColor = (current, max) => {
  if (current > max) return 'text-red-500';
  if (current > max * 0.9) return 'text-orange-500';
  return 'text-green-600';
};

const SeoMetadataSection = ({ seoData, setSeoData, resetToDefaults }) => {
  const handle = (field) => (e) => setSeoData(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Référencement (SEO)
        </CardTitle>
        <CardDescription>
          Ces informations apparaissent dans les résultats de recherche Google et les onglets du navigateur.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Meta Title */}
        <div className="space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <Label htmlFor="meta-title" className="text-base font-semibold">
                Titre du site (Meta Title)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Affiché dans l'onglet du navigateur et les résultats Google
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${getCharacterCountColor(seoData.meta_title.length, TITLE_MAX)}`}>
                {seoData.meta_title.length}/{TITLE_MAX}
              </span>
              <Button variant="ghost" size="sm" onClick={() => resetToDefaults('meta_title')} data-testid="reset-meta-title-btn">
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <Input
            id="meta-title"
            value={seoData.meta_title}
            onChange={handle('meta_title')}
            placeholder="Talent Partner | Recrutement de talents francophones"
            maxLength={80}
            data-testid="seo-meta-title-input"
          />
        </div>

        <Separator />

        {/* Meta Description */}
        <div className="space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <Label htmlFor="meta-description" className="text-base font-semibold">
                Description du site (Meta Description)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Texte affiché sous le titre dans les résultats de recherche
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${getCharacterCountColor(seoData.meta_description.length, DESC_MAX)}`}>
                {seoData.meta_description.length}/{DESC_MAX}
              </span>
              <Button variant="ghost" size="sm" onClick={() => resetToDefaults('meta_description')} data-testid="reset-meta-desc-btn">
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <Textarea
            id="meta-description"
            value={seoData.meta_description}
            onChange={handle('meta_description')}
            placeholder="Recrutez des collaborateurs francophones qualifiés..."
            rows={3}
            maxLength={200}
            data-testid="seo-meta-description-input"
          />
        </div>

        <Separator />

        {/* Google Snippet Preview */}
        <div className="space-y-3">
          <Label className="text-base font-semibold flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Aperçu dans Google
          </Label>
          <div className="p-4 bg-white border rounded-lg">
            <div className="space-y-1">
              <p className="text-[#1a0dab] text-lg hover:underline cursor-pointer truncate">
                {seoData.meta_title || 'Titre de votre page'}
              </p>
              <p className="text-[#006621] text-sm">
                {window.location.origin.replace('https://', '').replace('http://', '')}
              </p>
              <p className="text-[#545454] text-sm line-clamp-2">
                {seoData.meta_description || 'Description de votre page...'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SeoMetadataSection;
